import React, { useState } from 'react';
import { Check, ChevronLeft, Eye, EyeOff, Ear, EarOff } from 'lucide-react';

/* Pure helper: parse "HH:MM:SS.ms" / "MM:SS" / seconds into seconds */
export function parseTimestamp(ts) {
  if (!ts) return 0;
  const parts = ts.split(':');
  if (parts.length === 3) {
    const [h, m, rest] = parts;
    const [s, ms] = (rest || '0').split('.');
    return parseInt(h) * 3600 + parseInt(m) * 60 + parseFloat(s) + (ms ? parseFloat(`0.${ms}`) : 0);
  }
  if (parts.length === 2) {
    const [m, rest] = parts;
    const [s, ms] = (rest || '0').split('.');
    return parseInt(m) * 60 + parseFloat(s) + (ms ? parseFloat(`0.${ms}`) : 0);
  }
  return parseFloat(ts) || 0;
}

export function Rewind10Icon() {
  return (
    <svg width="54" height="54" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5C7.58 5 4 8.58 4 13s3.58 8 8 8 8-3.58 8-8" />
      <polyline points="4 5 4 9 8 9" />
      <text x="8.5" y="15.5" fill="currentColor" stroke="none" fontSize="7" fontWeight="700" fontFamily="Manrope,sans-serif" textAnchor="middle">10</text>
    </svg>
  );
}
export function Forward10Icon() {
  return (
    <svg width="54" height="54" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5c4.42 0 8 3.58 8 8s-3.58 8-8 8-8-3.58-8-8" />
      <polyline points="20 5 20 9 16 9" />
      <text x="12.5" y="15.5" fill="currentColor" stroke="none" fontSize="7" fontWeight="700" fontFamily="Manrope,sans-serif" textAnchor="middle">10</text>
    </svg>
  );
}
export function PrevEpisodeIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <rect x="3.5" y="5" width="2.5" height="14" rx="1" />
      <path d="M18 4L6 12l12 8V4z" opacity="0.9"/>
    </svg>
  );
}
export function NextEpisodeIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor" stroke="none">
      <path d="M6 4l12 8-12 8V4z" opacity="0.9"/>
      <rect x="18" y="5" width="2.5" height="14" rx="1" />
    </svg>
  );
}
export function EpisodesListIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <rect x="3" y="3" width="7" height="5" rx="1" />
      <rect x="3" y="10" width="7" height="5" rx="1" />
      <rect x="3" y="17" width="7" height="5" rx="1" />
      <line x1="13" y1="5.5" x2="21" y2="5.5" />
      <line x1="13" y1="12.5" x2="21" y2="12.5" />
      <line x1="13" y1="19.5" x2="18" y2="19.5" />
    </svg>
  );
}
export function SubtitlesIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <line x1="6" y1="13" x2="12" y2="13" />
      <line x1="14" y1="13" x2="18" y2="13" />
      <line x1="6" y1="17" x2="10" y2="17" />
      <line x1="12" y1="17" x2="18" y2="17" />
    </svg>
  );
}
export function FiltersIcon() {
  return (
    <svg width="45" height="45" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="4" y1="12" x2="20" y2="12" />
      <line x1="4" y1="18" x2="20" y2="18" />
      <circle cx="8" cy="6" r="2" fill="currentColor" />
      <circle cx="16" cy="12" r="2" fill="currentColor" />
      <circle cx="10" cy="18" r="2" fill="currentColor" />
    </svg>
  );
}

/* Hover scale wrapper for all buttons */
export function PlayerBtn({ children, onClick, className = '', testId, onMouseEnter, onMouseLeave }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={`text-white/80 hover:text-white transition-all duration-150 hover:scale-125 active:scale-95 ${className}`}
    >
      {children}
    </button>
  );
}

/* Audio/Subtitles Panel */
export function AudioSubPanel({ onClose, selectedAudio, setSelectedAudio, selectedSub, setSelectedSub, content }) {
  const audioTracks = content?.audio_tracks || [];
  const subtitles = content?.subtitles || [];
  const hasSubs = subtitles.length > 0;
  const burnedLang = content?.burned_subtitles_lang || '';
  const originalLang = content?.language || 'Français';

  return (
    <div className="absolute bottom-full right-0 mb-0 w-[500px] max-h-[70vh] bg-[#1a1a1a]/95 backdrop-blur-xl rounded-lg border border-white/10 overflow-hidden animate-scaleIn z-50" data-testid="audio-sub-panel" onClick={e => e.stopPropagation()}>
      <div className="flex h-full">
        {/* Audio */}
        <div className="flex-1 p-5 border-r border-white/10 overflow-y-auto max-h-[60vh]">
          <h3 className="text-base font-bold text-white mb-4">Audio</h3>
          <div className="space-y-1">
            {/* Default: original audio from video */}
            <button onClick={() => setSelectedAudio('__original__')} data-testid="audio-original-btn"
              className={`w-full text-left px-3 py-2.5 rounded text-sm transition-colors flex items-center gap-2 ${selectedAudio === '__original__' ? 'text-white' : 'text-white/50 hover:text-white/80'}`}>
              {selectedAudio === '__original__' && <Check size={16} className="text-white flex-none" />}
              <span className={selectedAudio !== '__original__' ? 'ml-6' : ''}>{audioTracks.length > 0 ? `${originalLang} (Original)` : originalLang}</span>
            </button>
            {audioTracks.map(t => (
              <button key={t.language} onClick={() => setSelectedAudio(t.language)}
                className={`w-full text-left px-3 py-2.5 rounded text-sm transition-colors flex items-center gap-2 ${selectedAudio === t.language ? 'text-white' : 'text-white/50 hover:text-white/80'}`}>
                {selectedAudio === t.language && <Check size={16} className="text-white flex-none" />}
                <span className={selectedAudio !== t.language ? 'ml-6' : ''}>{t.language}</span>
              </button>
            ))}
          </div>
        </div>
        {/* Subtitles */}
        <div className="flex-1 p-5 overflow-y-auto max-h-[60vh]">
          <h3 className="text-base font-bold text-white mb-4">Sous-titres</h3>
          <div className="space-y-1">
            <button onClick={() => setSelectedSub('Désactivés')} data-testid="subs-off-btn"
              className={`w-full text-left px-3 py-2.5 rounded text-sm transition-colors flex items-center gap-2 ${selectedSub === 'Désactivés' ? 'text-white' : 'text-white/50 hover:text-white/80'} ${burnedLang && !hasSubs ? 'cursor-default' : ''}`}>
              {selectedSub === 'Désactivés' && <Check size={16} className="text-white flex-none" />}
              <span className={selectedSub !== 'Désactivés' ? 'ml-6' : ''}>{burnedLang || 'Désactivés'}</span>
            </button>
            {hasSubs && subtitles.map(s => (
              <button key={s.language} onClick={() => setSelectedSub(s.language)}
                className={`w-full text-left px-3 py-2.5 rounded text-sm transition-colors flex items-center gap-2 ${selectedSub === s.language ? 'text-white' : 'text-white/50 hover:text-white/80'}`}>
                {selectedSub === s.language && <Check size={16} className="text-white flex-none" />}
                <span className={selectedSub !== s.language ? 'ml-6' : ''}>{s.language}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Episodes Panel */
export function EpisodesPanel({ content, currentEpisodeId, onSelectEpisode, onClose }) {
  const episodes = content?.episodes || [];
  const [season, setSeason] = useState(1);
  const seasons = [...new Set(episodes.map(e => e.season || 1))];

  return (
    <div className="absolute bottom-16 right-4 w-[420px] max-h-[70vh] bg-[#1a1a1a]/95 backdrop-blur-xl rounded-lg border border-white/10 overflow-hidden animate-scaleIn z-50" data-testid="episodes-panel" onClick={e => e.stopPropagation()}>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-4">
          {seasons.length > 1 && (
            <button onClick={() => setSeason(s => Math.max(1, s - 1))} className="text-white/50 hover:text-white">
              <ChevronLeft size={18} />
            </button>
          )}
          <h3 className="text-base font-bold text-white">Saison {season}</h3>
        </div>
        <div className="space-y-2 overflow-y-auto max-h-[55vh]">
          {episodes.filter(e => (e.season || 1) === season).map((ep, idx) => (
            <div key={ep.id} onClick={() => onSelectEpisode(ep)}
              className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition-colors ${ep.id === currentEpisodeId ? 'border border-white/20 bg-white/5' : 'hover:bg-white/5'}`}>
              <span className="text-lg text-white/40 w-6 text-center flex-none">{idx + 1}</span>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">{ep.title || `\u00c9pisode ${ep.episode}`}</p>
                {ep.id === currentEpisodeId && ep.thumbnail_url && (
                  <div className="mt-2 rounded overflow-hidden">
                    <img src={ep.thumbnail_url} alt="" className="w-full h-24 object-cover" />
                  </div>
                )}
                {ep.description && ep.id === currentEpisodeId && (
                  <p className="text-xs text-white/50 mt-2 line-clamp-3">{ep.description}</p>
                )}
              </div>
              <div className="w-16 h-0.5 bg-white/10 rounded mt-3 flex-none">
                <div className="h-full bg-[#E9641A] rounded" style={{ width: '30%' }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* Filters Panel - Maya+ exclusive */
export function FiltersPanel({ filters, setFilters, onClose, adminFilters }) {
  const [local, setLocal] = useState({ ...filters });

  const toggleFilter = (key) => {
    setLocal(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <div className="absolute bottom-full right-0 mb-0 w-[400px] max-h-[70vh] bg-[#1a1a1a]/95 backdrop-blur-xl rounded-lg border border-white/10 overflow-hidden animate-scaleIn z-50" onClick={e => e.stopPropagation()} data-testid="filters-panel">
      <div className="p-6">
        <h3 className="text-lg font-bold text-white mb-5">Vos filtres :</h3>
        <div className="space-y-3">
          {adminFilters.map(f => {
            const active = local[f.id];
            const isMute = f.action === 'mute';
            return (
              <div key={f.id} className="flex items-center justify-between py-2">
                <div>
                  <span className={`text-sm font-bold uppercase tracking-wide ${active ? 'text-white/30 line-through' : 'text-white'}`}>
                    {f.name}
                  </span>
                  <span className="text-[9px] text-white/20 ml-2">
                    {isMute ? 'son coupé' : 'scène coupée'}
                  </span>
                </div>
                <button onClick={() => toggleFilter(f.id)}
                  className={`p-1.5 rounded transition-colors ${active ? 'text-white/30' : 'text-white'}`}>
                  {isMute
                    ? (active ? <EarOff size={18} /> : <Ear size={18} />)
                    : (active ? <EyeOff size={18} /> : <Eye size={18} />)
                  }
                </button>
              </div>
            );
          })}
        </div>
        <div className="flex justify-end gap-3 mt-6">
          <button onClick={onClose} className="px-5 py-2 bg-white/10 text-white/70 rounded-md text-sm font-medium hover:bg-white/15 transition-colors" data-testid="filters-cancel">
            Annuler
          </button>
          <button onClick={() => { setFilters(local); onClose(); }} className="px-5 py-2 bg-[#E9641A] text-white rounded-md text-sm font-bold hover:bg-[#FE975E] transition-colors" data-testid="filters-save">
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
}
